import { vocabularyEntrySchema } from '@ghp/domain';
import { z } from 'zod';
import { bearer } from './http.js';
const vocabularyListSchema = z.array(vocabularyEntrySchema);
/**
 * L'administration du référentiel d'anomalies (M17) — l'écran « Règles anomalies » du back-office.
 *
 * ⚠️ **La lecture de la tablette n'est pas ici** : `sync.listVocabulary` sert les valeurs encore
 * proposables, c'est la descente hors réseau (D4). Les deux appels visent la même table et ne
 * rendent pas la même chose — l'un ce qu'un agent peut cocher, l'autre ce qu'un administrateur
 * peut corriger, retirées comprises. Les réunir obligerait l'un des deux à filtrer ce que l'autre
 * vient de demander.
 *
 * ⚠️ **Pas de `remove`, et ce n'est pas un manque** : une valeur se désactive (M17), elle ne se
 * supprime jamais — des règles (M18) et des anomalies archivées (M19) la citent. `deactivate` et
 * `reactivate` sont les deux bouts de ce que « supprimer » voudrait dire.
 *
 * Le jeton voyage **en argument**, comme les autres portées de gestion : le rendre explicite
 * oblige l'appelant à décider quoi faire quand il n'y en a pas, au lieu de collectionner des 401.
 */
export function createVocabularyEndpoints(http) {
    return {
        /**
         * Le référentiel **entier**, valeurs retirées comprises, trié par liste puis par rang côté
         * serveur.
         *
         * Pas de pagination : même dérogation assumée à D24 que `GET /users`, et pour la même raison —
         * le référentiel se compte en dizaines de lignes, et l'écran les affiche toutes, par liste.
         */
        listAll(accessToken) {
            return http.get('/vocabulary/all', vocabularyListSchema, bearer(accessToken));
        },
        /**
         * `201` : la valeur est créée en fin de sa liste.
         *
         * ⚠️ Le serveur répond `409` quand le code est déjà pris **dans cette liste** — un refus, pas
         * un avertissement : `UNIQUE (list, code)` est ce qui fait qu'une règle sait de quelle valeur
         * elle parle. L'écran a ce message à montrer, il ne peut pas le deviner.
         */
        create(accessToken, input) {
            return http.postJson('/vocabulary', input, vocabularyEntrySchema, bearer(accessToken));
        },
        /**
         * Corrige une valeur : le corps ne porte **que ce qui change**. Ni `code` ni `list` n'y
         * entrent — le premier est ce que les règles citent, le second est l'identité de la valeur.
         */
        update(accessToken, id, patch) {
            return http.patchJson(`/vocabulary/${id}`, patch, vocabularyEntrySchema, bearer(accessToken));
        },
        /**
         * Retire la valeur des écrans de saisie. Idempotent : la retirer deux fois n'est pas un refus.
         *
         * Un corps vide plutôt qu'aucun corps : le pipe du serveur fait passer les deux pour `{}`,
         * mais un `POST` sans `content-type` traverse mal certains proxys.
         */
        deactivate(accessToken, id) {
            return http.postJson(`/vocabulary/${id}/deactivate`, {}, vocabularyEntrySchema, bearer(accessToken));
        },
        /** Remet une valeur retirée dans les écrans de saisie. Idempotent, comme le retrait. */
        reactivate(accessToken, id) {
            return http.postJson(`/vocabulary/${id}/reactivate`, {}, vocabularyEntrySchema, bearer(accessToken));
        },
    };
}
//# sourceMappingURL=vocabulary.js.map